import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import Checkbox from '../../../forms/Checkbox'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { ButtonPrimary } from '../../Button'
import { Modify } from '@teinor/erp'
import { filterOnlyTableDataSelectedCheckboxLayout } from './functions'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { ColumnConfiguration } from '../VirtualTableListing/Subcomponents/types'
import { DefaultOrder, RowData, TableConfiguration } from '../types'
import VirtualTableListing from '../VirtualTableListing'
import { buildOrder, buildWhereBySearchbars, getNextOrder } from '../functions'

type TableCheckboxLayoutBaseProps = {
    translations: {
        buttonText: string
    }
    name: string
    onSubmit: (selectedIds: number[]) => void
    columns: ColumnConfiguration[]
}

export type TableCheckboxLayoutStateLessProps = TableCheckboxLayoutBaseProps & {
    tableData: TwinDictionary[]
    selectedTableData: TwinDictionary[]
    tableConfiguration: TableConfiguration
    changeOrder: (fieldName: string) => void
    changeSingleField: (fieldName: string, value: string | string[]) => void
    selectedIds: number[]
    columns: ColumnConfiguration[]
    selectAllLines: (checked: boolean) => void
    changeSelectedOneLine: (checked: boolean, rowIndex: number) => void
}

export const TableCheckboxLayoutStateLess: React.FC<TableCheckboxLayoutStateLessProps> = ({ columns, selectedIds, selectedTableData, translations, onSubmit, selectAllLines, changeSelectedOneLine, ...rest }) => {
    return (
        <Fragment>
            <div className='mb-40 flex justify-end'>
                {selectedTableData?.length ? <ButtonPrimary onClick={() => onSubmit(selectedIds)}>{translations.buttonText}</ButtonPrimary> : <div className='h-30 my-2'></div>}
            </div>
            <div className='relative flex flex-auto'>
                <VirtualTableListing columns={columns} headerChanges={{ changeOrder: rest.changeOrder, changeSingleField: rest.changeSingleField }} {...rest} />
            </div>
        </Fragment>
    )
}

type TableCheckboxLayoutProps = TableCheckboxLayoutBaseProps & TableCheckboxLayoutLogicProps & {}

const TableCheckboxLayout: React.FC<TableCheckboxLayoutProps> = ({ columns, ...rest }) => {
    const { tableData, parsedColumns, ...restLogic } = useTableCheckboxLayoutLogic({ columns, ...rest })
    return (
        <TableCheckboxLayoutStateLess tableData={tableData || []} columns={parsedColumns} {...rest} {...restLogic} />
    )
}

interface TableCheckboxLayoutLogicProps {
    getDataFrom: string
    columns: ColumnConfiguration[]
    parseData?: (data: TwinDictionary[]) => TwinDictionary[]
    defaultOrder?: DefaultOrder
    extraWhereParams?: TwinDictionary
}

const useTableCheckboxLayoutLogic = ({ defaultOrder, extraWhereParams, getDataFrom, columns, parseData }: TableCheckboxLayoutLogicProps) => {
    const [tableConfiguration, setTableConfiguration] = useState<TableConfiguration>({
        generalSearchbar: '',
        order: {},
        offset: 0,
        singleFieldsSearch: {},
        petitionNumber: 0
    })
    const [tableData, setTableData] = useState<TwinDictionary[] | null>(null)
    const changeOrder = useCallback((fieldName: string) => {
        setTableConfiguration((oldTableConfiguration) => {
            const newOrder = getNextOrder(fieldName, oldTableConfiguration.order, defaultOrder?.order || 'desc')
            return {
                ...oldTableConfiguration,
                order: { ...newOrder }
            }
        })
    }, [setTableConfiguration, defaultOrder?.order])

    const changeSingleField = useCallback((fieldName: string, value: string | string[]) => {
        setTableConfiguration((oldTableConfiguration) => {
            const copyOld = JSON.parse(JSON.stringify(oldTableConfiguration))
            return {
                ...copyOld,
                offset: 0,
                singleFieldsSearch: {
                    ...copyOld.singleFieldsSearch,
                    [fieldName]: value
                }
            }
        })
    }, [setTableConfiguration])

    const updateDataTable = useCallback(async () => {
        const orderBuiled = buildOrder(tableConfiguration.order, columns)
        const whereBySearchBars = buildWhereBySearchbars(
            { generalSearchbar: tableConfiguration.generalSearchbar, singleFieldsSearch: tableConfiguration.singleFieldsSearch },
            columns
        )
        let apiTableData = await twinFetchPostJSON(getDataFrom, {
            where: {
                ...whereBySearchBars,
                ...extraWhereParams
            },
            order: orderBuiled.length ? orderBuiled : [[defaultOrder?.defaultOrderField || 'id', defaultOrder?.order || 'desc']],
        })
        if (parseData) {
            apiTableData = parseData(apiTableData)
        }
        setTableData([...apiTableData])
    }, [tableConfiguration.generalSearchbar, tableConfiguration.singleFieldsSearch, tableConfiguration.order, getDataFrom, setTableData, defaultOrder?.defaultOrderField, defaultOrder?.order, columns, extraWhereParams, parseData])

    const selectAllLines = useCallback((checked: boolean) => {
        setTableData((old) => {
            const tableCopy = JSON.parse(JSON.stringify(old))
            for (const key in tableCopy) {
                const element = tableCopy[key]
                element.selected = checked
            }
            return [...tableCopy]
        })
    }, [setTableData])

    const changeSelectedOneLine = useCallback((checked: boolean, rowIndex: number) => {
        setTableData((old) => {
            const tableCopy = JSON.parse(JSON.stringify(old))
            tableCopy[rowIndex]['selected'] = checked
            return [...tableCopy]
        })
    }, [setTableData])

    const { selectedTableData, selectedIds } = filterOnlyTableDataSelectedCheckboxLayout(tableData)
    const tableDataLength = tableData ? tableData.length : 0
    const allSelected = tableDataLength > 0 && selectedTableData?.length === tableDataLength
    const parsedColumns = [...columns]
    parsedColumns.unshift(
        {
            id: 'selected',
            dataKey: allSelected ? 'selected_full' : 'selected_not_full',
            label: () => <Checkbox label={''} onChange={selectAllLines} checked={tableDataLength > 0 && selectedTableData?.length === tableDataLength} />,
            width: 50,
            customRender: (value: string, allRowData: RowData) => <Checkbox label={''} checked={allRowData?.selected} onChange={(checked) => changeSelectedOneLine(checked, allRowData.absoluteIndex || allRowData.rowIndex)} />
        }
    )

    useEffect(() => {
        updateDataTable()
    }, [tableConfiguration.singleFieldsSearch, tableConfiguration.order, updateDataTable])

    return { tableData, selectedTableData, selectAllLines, changeSelectedOneLine, changeOrder, changeSingleField, tableConfiguration, selectedIds, parsedColumns }
}

type TableCheckboxLayoutLinkProps = Modify<TableCheckboxLayoutProps, TableCheckboxLayoutLinkPropsLogic & {
    onSubmit?: never
}>

export const TableCheckboxLayoutLink: React.FC<TableCheckboxLayoutLinkProps> = ({ baseLink, ...rest }) => {
    const { generateLink } = useTableCheckboxLayoutLinkLogic({ baseLink })
    return (<TableCheckboxLayout onSubmit={generateLink} {...rest} />)
}

interface TableCheckboxLayoutLinkPropsLogic {
    baseLink: string
}

const useTableCheckboxLayoutLinkLogic = ({ baseLink }: TableCheckboxLayoutLinkPropsLogic) => {
    const navigate = useNavigate()
    const generateLink: TableCheckboxLayoutProps['onSubmit'] = useCallback((selectedIds) => {
        const link = selectedIds.join('-')
        navigate(baseLink + link)
    }, [navigate, baseLink])

    return { generateLink }
}


export default TableCheckboxLayout